import React, { useMemo } from 'react';
import { Link, generatePath } from 'react-router-dom';
import { Stack } from '@hub/stack';
import PotentialLink from '@scentregroup/shared/hub-components/potential-link';
import { getProductListingIssue } from '../../helpers/stock-level-issue-messages';
import { displayStringForPriceRange } from '../../helpers/product-price';
import { stringToSlug } from '../../helpers/slug';
import { generateStoreFrontUrl } from '../../helpers/store';
import ConditionalIssueText from '../conditional-issue-text';
import { CloudinaryImage, IMAGE_SIZES_PRODUCT_CARD } from '../../hub-components/cloudinary-image';
import { isDefinedAndNotNull } from '../../lib';
import { useAnalytics } from '../../analytics';
import { makeProductClickedForVariantGroup } from '../../analytics-segment/events/product-clicked';
import style from './index.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function commonApplicablePromotionsForListing(variants) {
  var _variants$0$promotion, _variants$, _variants$$promotions;

  function allVariantsHavePromotion(_ref) {
    let {
      campaignId,
      title
    } = _ref;
    return variants.every(variant => {
      var _variant$promotions;

      return variant === null || variant === void 0 ? void 0 : (_variant$promotions = variant.promotions) === null || _variant$promotions === void 0 ? void 0 : _variant$promotions.find(promotion => promotion.campaignId === campaignId && promotion.title === title);
    });
  }

  return (_variants$0$promotion = (_variants$ = variants[0]) === null || _variants$ === void 0 ? void 0 : (_variants$$promotions = _variants$.promotions) === null || _variants$$promotions === void 0 ? void 0 : _variants$$promotions.filter(allVariantsHavePromotion)) !== null && _variants$0$promotion !== void 0 ? _variants$0$promotion : [];
} // This duplicates what's in /src/client/path.ts of the checkout app

export const PRODUCT = "/shop/product/:shopId/:productId/:productTitle";
export const ProductCard = _ref2 => {
  var _product$variant, _shop$retailer, _product$brand, _product$brand2, _product$brand3, _product$brand4;

  let {
    product,
    analytics,
    priceRange,
    discountedPriceRange,
    discountPercentageRange,
    onClick
  } = _ref2;
  const {
    shop
  } = product;
  const {
    trackEvent
  } = useAnalytics();
  const issue = useMemo(() => getProductListingIssue(product === null || product === void 0 ? void 0 : product.issues), [product === null || product === void 0 ? void 0 : product.issues]);
  const commonPromotions = commonApplicablePromotionsForListing([product.variant, ...product.otherVariants].filter(isDefinedAndNotNull));
  const applicablePromotions = commonPromotions.length ? commonPromotions : (_product$variant = product.variant) === null || _product$variant === void 0 ? void 0 : _product$variant.promotions;
  const storeFrontUrl = generateStoreFrontUrl((_shop$retailer = shop.retailer) === null || _shop$retailer === void 0 ? void 0 : _shop$retailer.sys.id);
  const imgAlt = (_product$brand = product.brand) !== null && _product$brand !== void 0 && _product$brand.name ? "".concat(product.name, " - ").concat((_product$brand2 = product.brand) === null || _product$brand2 === void 0 ? void 0 : _product$brand2.name) : "".concat(product.name);
  const productPath = generatePath("".concat(PRODUCT), {
    productTitle: (_product$brand3 = product.brand) !== null && _product$brand3 !== void 0 && _product$brand3.name ? stringToSlug("".concat(product.brand.name, "-").concat(product.name)) : stringToSlug(product.name),
    productId: product.variantGroupCode,
    shopId: "".concat(shop.id)
  });
  const trackingParams = new URLSearchParams();

  if (analytics !== null && analytics !== void 0 && analytics.queryID) {
    trackingParams.append('aqid', analytics.queryID);
  }

  if (analytics !== null && analytics !== void 0 && analytics.indexName) {
    trackingParams.append('aqin', analytics.indexName);
  }

  if (analytics !== null && analytics !== void 0 && analytics.position) {
    trackingParams.append('aqpos', String(analytics.position));
  }

  return /*#__PURE__*/_jsxs("div", {
    className: style.productCard,
    "data-test-id": "product-card",
    "data-id": "product-card_index_ProductCard_div",
    children: [/*#__PURE__*/_jsxs(Link, {
      className: style.productLinkWrapper,
      to: "".concat(productPath, "#").concat(trackingParams.toString()),
      onClick: () => {
        trackEvent(makeProductClickedForVariantGroup(product, analytics === null || analytics === void 0 ? void 0 : analytics.sortMethod, analytics === null || analytics === void 0 ? void 0 : analytics.listId));
        onClick === null || onClick === void 0 ? void 0 : onClick();
      },
      "data-id": "product-card_index_ProductCard_div_Link",
      children: [/*#__PURE__*/_jsx(CloudinaryImage, {
        ratio: 0.75,
        imageSetOrImage: {
          url: product.image || '',
          type: 'SQUARE',
          alt: imgAlt
        },
        resizeMode: "pad",
        sizes: IMAGE_SIZES_PRODUCT_CARD,
        "data-id": "product-card_index_ProductCard_Link_CloudinaryImage"
      }), /*#__PURE__*/_jsx("div", {
        className: style.productName,
        "data-id": "product-card_index_ProductCard_Link_div",
        children: (_product$brand4 = product.brand) !== null && _product$brand4 !== void 0 && _product$brand4.name ? /*#__PURE__*/_jsxs(Stack, {
          gap: "spacing-xs",
          "aria-label": "".concat(product.brand.name, " - ").concat(product.name),
          "data-id": "product-card_index_ProductCard_Stack",
          children: [/*#__PURE__*/_jsx("div", {
            "aria-hidden": true,
            "data-id": "product-card_index_ProductCard_Stack_div",
            children: product.brand.name
          }), /*#__PURE__*/_jsx("div", {
            "aria-hidden": true,
            "data-id": "product-card_index_ProductCard_Stack_div_6",
            children: product.name
          })]
        }) : product.name
      }), /*#__PURE__*/_jsxs("div", {
        className: style.pricesWrapper,
        "data-id": "product-card_index_ProductCard_Link_div_7",
        children: [/*#__PURE__*/_jsx("div", {
          className: discountPercentageRange ? style.discountedPrice : style.price,
          "data-id": "product-card_index_ProductCard_div_div_8",
          children: displayStringForPriceRange(discountedPriceRange !== null && discountedPriceRange !== void 0 ? discountedPriceRange : priceRange)
        }), discountedPriceRange && /*#__PURE__*/_jsx("div", {
          className: style.originalPrice,
          "data-id": "product-card_index_ProductCard_div_9",
          children: displayStringForPriceRange(priceRange)
        })]
      })]
    }), (applicablePromotions === null || applicablePromotions === void 0 ? void 0 : applicablePromotions[0]) && /*#__PURE__*/_jsx("div", {
      className: style.applicablePromotion,
      "data-id": "product-card_index_ProductCard_div_10",
      children: applicablePromotions[0].title
    }), /*#__PURE__*/_jsxs("div", {
      className: style.productRetailer,
      "data-id": "product-card_index_ProductCard_div_div_11",
      children: ["Sold by\xA0", /*#__PURE__*/_jsx(PotentialLink, {
        className: style.shopName,
        href: storeFrontUrl,
        "data-id": "product-card_index_ProductCard_div_PotentialLink",
        children: shop.name
      })]
    }), issue && /*#__PURE__*/_jsx(ConditionalIssueText, {
      issue: issue,
      wrapperClass: style.issueWrapper,
      "data-id": "product-card_index_ProductCard_ConditionalIssueText"
    })]
  });
};
export default ProductCard;